import { PageShell, cn } from '@chopchop-se/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Suspense, useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useNavigate } from 'react-router';

import { buildPortalNav } from '../app/nav';
import { logout } from '../auth/auth-api';
import { useAuthStore, usePermissionCheck } from '../auth/auth-store';
import { FullPageSpinner } from '../components/FullPageSpinner';
import { AppTopBar } from './AppTopBar';

/**
 * The restaurant portal chrome (`/portal`).
 *
 * Deliberately NOT the admin shell: a restaurant manager on a phone in a busy
 * kitchen gets four large tap targets in a horizontal bar, no sidebar, no
 * warehouse vocabulary. Same auth, same fetch client, same live updates.
 */
export function PortalLayout(): ReactNode {
  const { t } = useTranslation(['shell', 'common']);
  const can = usePermissionCheck();
  const setAnonymous = useAuthStore((s) => s.setAnonymous);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const items = useMemo(() => buildPortalNav(can), [can]);
  // Anyone with warehouse chrome gets a way back; a pure `bestallare` does not
  // (there is nothing there for them).
  const showAdminLink = can(['orders.outbound.manage', 'warehouse.stock.read']);

  function handleSignOut(): void {
    void (async () => {
      try {
        await logout();
      } finally {
        setAnonymous();
        queryClient.clear();
        void navigate('/login', { replace: true });
      }
    })();
  }

  return (
    <PageShell
      contained
      header={
        <AppTopBar
          systemLabel={t('shell:layout.portalApp')}
          crossLink={showAdminLink ? { to: '/', label: t('shell:layout.adminApp') } : undefined}
          onSignOut={handleSignOut}
        />
      }
    >
      <div className="mx-auto w-full max-w-5xl px-4 pb-16 sm:px-6">
        <nav
          aria-label={t('shell:layout.mainNavigation')}
          className="sticky top-0 z-10 -mx-4 mb-6 flex gap-2 overflow-x-auto border-b border-surface-border bg-surface/80 px-4 py-3 backdrop-blur sm:mx-0 sm:rounded-2xl sm:border sm:px-3"
        >
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end ?? false}
              className={({ isActive }) =>
                cn(
                  'flex shrink-0 items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-brand-600 text-white shadow-card'
                    : 'text-ink-light hover:bg-brand-600/[0.08] hover:text-ink',
                )
              }
            >
              {item.icon ? <item.icon className="size-4" aria-hidden /> : null}
              <span>{t(item.labelKey)}</span>
            </NavLink>
          ))}
        </nav>

        <main id="main">
          <Suspense fallback={<FullPageSpinner />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
    </PageShell>
  );
}
